import { cn } from "@/lib/cn";
import { Check } from "@/icons";
import { cva } from "class-variance-authority";

/** Palette of a checked indicator. */
export type IndicatorTone = "primary" | "secondary";

/** Box size: `sm` 20px, `md` 24px. */
export type IndicatorSize = "sm" | "md";

export type IndicatorProps = {
  checked: boolean;
  tone?: IndicatorTone;
  size?: IndicatorSize;
  disabled?: boolean;
  className?: string;
};

const boxVariants = cva(
  "inline-grid shrink-0 place-items-center border-2 transition-[background-color,border-color] duration-fast ease-standard",
  {
    variants: {
      size: { sm: "h-5 w-5", md: "h-6 w-6" },
      checked: { true: "", false: "border-card-text/50 bg-card" },
      disabled: { true: "opacity-50", false: "" },
    },
    defaultVariants: { size: "md", checked: false, disabled: false },
  },
);

const CHECKED_TONE: Record<IndicatorTone, { border: string; fill: string; mark: string }> = {
  primary: { border: "border-primary", fill: "bg-primary", mark: "text-primary-foreground" },
  secondary: { border: "border-secondary", fill: "bg-secondary", mark: "text-secondary-foreground" },
};

/** Visual checkbox mark (decorative, `aria-hidden`); pair it with an element that owns the semantics. */
export function CheckIndicator({ checked, tone = "primary", size = "md", disabled = false, className }: IndicatorProps) {
  const palette = CHECKED_TONE[tone];
  return (
    <span
      aria-hidden
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        boxVariants({ size, checked, disabled }),
        "rounded-badge",
        checked && [palette.border, palette.fill],
        className,
      )}
    >
      <Check
        weight="bold"
        className={cn(
          size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
          palette.mark,
          "transition-transform duration-fast ease-enter",
          checked ? "scale-100" : "scale-0",
        )}
      />
    </span>
  );
}

/** Visual radio mark (decorative, `aria-hidden`); pair it with an element that owns the semantics. */
export function RadioIndicator({ checked, tone = "primary", size = "md", disabled = false, className }: IndicatorProps) {
  const palette = CHECKED_TONE[tone];
  return (
    <span
      aria-hidden
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        boxVariants({ size, checked, disabled }),
        "rounded-full",
        checked && [palette.border, "bg-card"],
        className,
      )}
    >
      <span
        className={cn(
          "rounded-full transition-transform duration-fast ease-enter",
          size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3",
          palette.fill,
          checked ? "scale-100" : "scale-0",
        )}
      />
    </span>
  );
}
